import { Stack, Typography } from "@mui/joy";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgentObj } from "@/types/interfaces/agents";
import {
  NameCellRenderer,
  OptionalValueCellRenderer,
  WebsiteUrlCellRenderer,
  YesNoCellRenderer,
  SpecialistTextCellRenderer,
  EmailCellRenderer,
  LinkedCellRenderer,
  StatusCellRenderer,
  AgentCellRenderer,
  TwoLineCellRenderer,
} from "./utils";
import { formatPhone } from "./utils/utils";
import { STANDARD_DOMAIN } from "@/types/constants/common";
import NoteCellRenderer from "./utils/NoteCellRenderer";
import {
  AGREEMENT_OPTIONS,
  AGENT_TYPE_OPTIONS,
  CERTIFICATION_OPTIONS,
  DISPLAY_SALES_OPTIONS,
  EXPERTISE_LEVEL_OPTIONS,
  FB_GROUPS_OPTIONS,
  HAS_SABRE_OPTIONS,
  YESNO_BOOLEAN,
  MENTOR_PROGRAM_OPTIONS,
  PROVINCE_OPTIONS,
  STATUS_OPTIONS,
  TOUR_OPS_INSURANCE_OPTIONS,
  YES_NO_OPTIONS,
} from "./constants";
import Thumbnail from "@/components/Thumbnail/Thumbnail";
import SelectFilter from "@/components/DataGrid/CustomFilters/SelectFilters/SelectFilter";
import SelectFloatingFilter from "@/components/DataGrid/CustomFilters/SelectFilters/SelectFloatingFilter";
import { CERTIFICATIONS } from "../constants";

export const ONBOARDING_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 130,
    headerName: "Title",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    hide: true,
  },
  {
    field: "lastname",
    width: 200,
    headerName: "Last Name",
    hide: true,
  },
  {
    field: "agentId",
    width: 140,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
    pinned: "left",
  },
  {
    field: "clientlinqBranch",
    width: 140,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
    pinned: "left",
  },
  {
    field: "agentType",
    width: 150,
    headerName: "Type",
    floatingFilter: true,
    filter: SelectFilter,
    floatingFilterComponent: SelectFloatingFilter,
    filterParams: {
      options: AGENT_TYPE_OPTIONS,
    },
    pinned: "left",
  },
  {
    field: "note",
    width: 80,
    headerName: "Notes",
    sortable: false,
    suppressMenu: true,
    cellRenderer: NoteCellRenderer,
  },
  {
    field: "risingStarLevel",
    width: 110,
    headerName: "Level",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "agreement",
    width: 220,
    headerName: "Agreement",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGREEMENT_OPTIONS,
    },
  },
  {
    field: "ttandEmail",
    width: 150,
    headerName: "TTAND Email",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "ttandExtension",
    width: 150,
    headerName: "Ext.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "brandedAs", width: 200, headerName: "Branded As", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "associate.reportingPrimaryAgent",
    width: 200,
    headerName: "PA for AA",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "associate.commissionSplit",
    width: 140,
    headerName: "Split",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "streetAddress",
    width: 200,
    headerName: "Home Address",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "province",
    width: 140,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  { field: "postalCode", width: 140, headerName: "Postal #", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "dates.contractCommencement",
    width: 135,
    headerName: "Contract Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "dates.birthDate",
    width: 135,
    headerName: "Birth Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "months",
    width: 140,
    headerName: "Months",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
  },
  {
    field: "ttandEmailAlias",
    width: 150,
    headerName: "Alias Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "emailForwardedTo",
    width: 150,
    headerName: "Forwarding Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "businessEmail",
    width: 150,
    headerName: "Business Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "otherEmail",
    width: 150,
    headerName: "Personal Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "backupAgentEmail",
    width: 150,
    headerName: "Backup Agent Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "cellPhone",
    width: 170,
    headerName: "Cell Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "homePhone",
    width: 180,
    headerName: "Home Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "businessPhone",
    width: 180,
    headerName: "Business Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "extensionForwardedTo",
    width: 180,
    headerName: "Ext Forward Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },

  {
    field: "displaySales",
    width: 150,
    headerName: "Display Sales",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: DISPLAY_SALES_OPTIONS,
    },
  },
  { field: "nickname", width: 150, headerName: "Nickname", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "dates.monthlyFeesChargeStarted",
    width: 180,
    headerName: "Monthly Fee Charge Started",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "dates.annualFeesChargeMonth",
    width: 150,
    headerName: "Annual Fee Charge Month",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "dates.trainingDate",
    width: 150,
    headerName: "Training Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "usClientlinqId",
    width: 150,
    headerName: "US CL ID",
    floatingFilter: true,
    valueGetter: (params) => params.data?.usClientlinqId ?? "",
    filter: "agTextColumnFilter",
  },
  {
    field: "usClientlinqBranch",
    width: 150,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "manulifeBranch",
    width: 150,
    headerName: "Manulife B.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "tugoBranch", width: 150, headerName: "Tugo B.", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "dates.businessCardCompletion",
    width: 150,
    headerName: "BIZ Card Completion",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  { field: "onTTANDFacebook", width: 150, headerName: "FB Groups", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "mentorAgentProgram",
    width: 150,
    headerName: "Mentorship",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: MENTOR_PROGRAM_OPTIONS,
    },
  },
  {
    field: "iataCard",
    width: 200,
    headerName: "IATA Card",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "corpCC", width: 150, headerName: "Corp CC", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "masterBusinessLicense",
    width: 150,
    headerName: "Biz License",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "license",
    width: 150,
    headerName: "Provincial License",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "licenses.tico",
    width: 150,
    headerName: "TICO Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "licenses.bccp",
    width: 150,
    headerName: "BCCP Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "licenses.opc",
    width: 150,
    headerName: "OPC Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "bricksAndMortar",
    width: 150,
    headerName: "Bricks & Mortar",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "insuranceLicense",
    width: 350,
    headerName: "Insurance License",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "hasSabre",
    width: 150,
    headerName: "Has GDS",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: HAS_SABRE_OPTIONS,
    },
  },
  {
    field: "tourOpsInsurance",
    width: 150,
    headerName: "Tour Ops Insurance",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: TOUR_OPS_INSURANCE_OPTIONS,
    },
  },
  {
    field: "websiteURL",
    width: 350,
    headerName: "Website URL",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: WebsiteUrlCellRenderer,
  },
  {
    field: "dates.websiteCompletion",
    width: 150,
    headerName: "B2C Website Completion",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "referralNumber",
    width: 150,
    headerName: "Referral #",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "associateApplicationURL",
    width: 200,
    headerName: "AP Applic. URL",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "dates.terminationDate",
    width: 150,
    headerName: "Termination Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "callPlans.dedicatedLocalNumber",
    width: 180,
    headerName: "Dedi. Local Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "callPlans.ttandPhone",
    width: 180,
    headerName: "TTAND Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "callPlans.tollfreeNumber",
    width: 180,
    headerName: "Toll Free Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "callPlans.outgoingCallPlan",
    width: 180,
    headerName: "Outgoing Lines",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "callPlans.incomingCallPlan",
    width: 180,
    headerName: "Incoming Lines",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "office365Subscription",
    width: 150,
    headerName: "Office 365",
    floatingFilter: true,
    filter: SelectFilter,
    floatingFilterComponent: SelectFloatingFilter,
    suppressMenu: true,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "secretAgentString",
    width: 140,
    headerName: "Private Agent",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YES_NO_OPTIONS,
    },
    hide: true,
  },
  {
    field: "fitTrip.consent",
    width: 200,
    headerName: "Fit Trip Signup",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "fitTrip.level",
    width: 200,
    headerName: "Fit Trip Level",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "agencyLogo",
    width: 200,
    headerName: "Agency Logo",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "status",
    width: 140,
    headerName: "Status",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: STATUS_OPTIONS,
    },
  },
];

// Laid out as a user list: each row reads like a person (avatar, name, handle), with related details paired in two-line
// cells. The single-value columns those pairs replace are still here, hidden, for filtering, sorting or showing again
const AGENT_TYPE_LABELS: Record<string, string> = { PA: "Primary agent", AA: "Associate" };

export const SUPPORT_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "name",
    width: 300,
    headerName: "Agent",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: AgentCellRenderer,
    pinned: "left",
  },
  {
    field: "status",
    width: 110,
    headerName: "Status",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: STATUS_OPTIONS,
    },
    cellRenderer: StatusCellRenderer,
  },
  {
    field: "agentType",
    width: 200,
    headerName: "Role",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGENT_TYPE_OPTIONS,
    },
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      primary: (a: AgentObj) => AGENT_TYPE_LABELS[a.agentType || ""] || a.agentType,
      secondary: (a: AgentObj) =>
        a.agentType === "AA" && a.associate?.reportingPrimaryAgent && a.associate.reportingPrimaryAgent !== "-"
          ? `under ${a.associate.reportingPrimaryAgent}`
          : undefined,
    },
  },
  {
    field: "cellPhone",
    width: 210,
    headerName: "Contact",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      copy: true,
      secondary: (a: AgentObj) => {
        const ext = a.ttandExtension && a.ttandExtension !== "-" ? `Ext ${a.ttandExtension}` : "";
        const cell = String(a.cellPhone || "")
          .replace(/\D/g, "")
          .slice(-10);
        const fwd = String(a.extensionForwardedTo || "").replace(/\D/g, "");
        // Only worth a mention when the extension rings somewhere other than their cell
        const fwdNote =
          fwd.length > 1 && (!cell || !fwd.endsWith(cell)) ? `→ ${formatPhone(a.extensionForwardedTo)}` : "";
        return [ext, fwdNote].filter(Boolean).join(" ");
      },
    },
  },
  {
    field: "agentId",
    width: 130,
    headerName: "ID / Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      primary: (a: AgentObj) => (a.agentId ? `#${a.agentId}` : undefined),
      secondary: (a: AgentObj) => (a.clientlinqBranch ? `Branch ${a.clientlinqBranch}` : undefined),
    },
  },
  {
    field: "city",
    width: 170,
    headerName: "Location",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      secondary: (a: AgentObj) => a.province,
    },
  },
  {
    field: "agreement",
    width: 200,
    headerName: "Agreement",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGREEMENT_OPTIONS,
    },
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      secondary: (a: AgentObj) =>
        [
          a.risingStarLevel !== undefined && a.risingStarLevel !== null && `Level ${a.risingStarLevel}`,
          a.months && a.months > 0 && `${a.months} mo`,
        ]
          .filter(Boolean)
          .join(" · "),
    },
  },
  {
    field: "licenses.tico",
    headerName: "Licensing",
    width: 260,
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.licenses?.tico,
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      primary: (a: AgentObj) => (a.licenses?.tico ? `TICO ${a.licenses.tico}` : undefined),
      secondary: (a: AgentObj) => a.insuranceLicense,
    },
    // The insurance note is often a sentence; the full text shows on hover
    tooltipField: "insuranceLicense",
  },
  {
    field: "manulifeBranch",
    width: 160,
    headerName: "Insurance B.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: TwoLineCellRenderer,
    cellRendererParams: {
      primary: (a: AgentObj) => (a.manulifeBranch ? `Manulife ${a.manulifeBranch}` : undefined),
      secondary: (a: AgentObj) => (a.tugoBranch ? `Tugo ${a.tugoBranch}` : undefined),
    },
  },

  // Hidden by default: the single values the cells above pair up
  {
    field: "ttandEmail",
    width: 170,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 100,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "associate.reportingPrimaryAgent",
    width: 180,
    headerName: "PA for AA",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "ttandExtension",
    width: 90,
    headerName: "Ext.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "extensionForwardedTo",
    width: 150,
    headerName: "Ext. Fwd To",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "emailForwardedTo",
    width: 200,
    headerName: "Email Fwd To",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "province",
    width: 110,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
    hide: true,
  },
  {
    field: "risingStarLevel",
    width: 90,
    headerName: "Level",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "months",
    width: 100,
    headerName: "Months",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    hide: true,
  },
  {
    field: "insuranceLicense",
    width: 260,
    headerName: "Insurance License",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    tooltipField: "insuranceLicense",
    hide: true,
  },
  {
    field: "tugoBranch",
    width: 110,
    headerName: "Tugo B.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "hasSabre",
    width: 110,
    headerName: "Has GDS",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: HAS_SABRE_OPTIONS,
    },
    hide: true,
  },
  {
    field: "brandedAs",
    width: 250,
    headerName: "Branded As",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "note",
    width: 80,
    headerName: "Notes",
    sortable: false,
    suppressMenu: true,
    cellRenderer: NoteCellRenderer,
    hide: true,
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "nickname",
    width: 150,
    headerName: "Name Alias",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "identifierNames",
    width: 250,
    headerName: "Identifier Names",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "usClientlinqBranch",
    width: 110,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "homePhone",
    width: 150,
    headerName: "Home Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "streetAddress",
    width: 250,
    headerName: "Home Address",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "postalCode",
    width: 110,
    headerName: "Postal #",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "websiteURL",
    width: 250,
    headerName: "Website URL",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "dates.contractCommencement",
    width: 140,
    headerName: "Contract Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "dates.trainingDate",
    width: 140,
    headerName: "Training Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "dates.monthlyFeesChargeStarted",
    width: 180,
    headerName: "Monthly Fee Charge Started",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "dates.annualFeesChargeMonth",
    width: 150,
    headerName: "Annual Fee Charge Month",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "dates.businessCardCompletion",
    width: 150,
    headerName: "BIZ Card Completion",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "displaySales",
    width: 130,
    headerName: "Display Sales",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: DISPLAY_SALES_OPTIONS,
    },
    hide: true,
  },
  {
    field: "mentorAgentProgram",
    width: 180,
    headerName: "Mentor Agent Program",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: MENTOR_PROGRAM_OPTIONS,
    },
    hide: true,
  },
  {
    field: "onTTANDFacebook",
    width: 130,
    headerName: "On Facebook",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "zipProgramMember",
    width: 130,
    headerName: "Has FlexPay",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    cellRenderer: YesNoCellRenderer,
    hide: true,
  },
  {
    field: "masterBusinessLicense",
    width: 200,
    headerName: "Master BIZ License",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "licenses.bccp",
    headerName: "BCCP Number",
    width: 120,
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.licenses?.bccp,
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "licenses.opc",
    headerName: "OPC Number",
    width: 120,
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.licenses?.opc,
    cellRenderer: OptionalValueCellRenderer,
    hide: true,
  },
  {
    field: "tourOpsInsurance",
    width: 150,
    headerName: "Tour Ops Insurance",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: TOUR_OPS_INSURANCE_OPTIONS,
    },
    hide: true,
  },
  {
    field: "bricksAndMortar",
    width: 130,
    headerName: "Bricks & Mortar",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "iataCard",
    width: 150,
    headerName: "IATA Card",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "corpCC",
    width: 130,
    headerName: "Corp CC",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "referralNumber",
    width: 110,
    headerName: "Referral #",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "callPlans.ttandPhone",
    width: 150,
    headerName: "TTAND Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "callPlans.tollfreeNumber",
    width: 150,
    headerName: "Tollfree Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "callPlans.dedicatedLocalNumber",
    width: 180,
    headerName: "Dedicated Local Number",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "callPlans.outgoingCallPlan",
    width: 130,
    headerName: "Outgoing Plan",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "callPlans.incomingCallPlan",
    width: 130,
    headerName: "Incoming Plan",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
];

export const FINANCE_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "agentId",
    width: 150,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "note",
    width: 80,
    headerName: "Notes",
    sortable: false,
    suppressMenu: true,
    cellRenderer: NoteCellRenderer,
  },
  {
    field: "risingStarLevel",
    width: 110,
    headerName: "Level",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "payrollNumbers",
    width: 140,
    headerName: "Payroll Numbers",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueFormatter: (params) => {
      const payrollNumbers = params.value;
      if (Array.isArray(payrollNumbers) && payrollNumbers.length > 0) {
        return payrollNumbers.join(", ");
      }
      return "-";
    },
  },
  {
    field: "bank.accountNumber",
    width: 150,
    headerName: "Bank Account #",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "bank.paymentMethod",
    width: 150,
    headerName: "Payment Method",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "clientlinqBranch",
    width: 180,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "usClientlinqBranch",
    width: 180,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "agreement",
    width: 220,
    headerName: "Agreement",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGREEMENT_OPTIONS,
    },
  },
  {
    field: "agentType",
    width: 140,
    headerName: "Type",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGENT_TYPE_OPTIONS,
    },
  },
  {
    field: "ttandEmail",
    width: 150,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  {
    field: "associate.reportingPrimaryAgent",
    width: 200,
    headerName: "PA for AA",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "otherEmail",
    width: 250,
    headerName: "Other Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "cellPhone", width: 150, headerName: "Cell Phone", floatingFilter: true, filter: "agTextColumnFilter" },
  { field: "ttandExtension", width: 140, headerName: "Ext.", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "streetAddress",
    width: 275,
    headerName: "Home Address",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "province",
    width: 150,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  { field: "postalCode", width: 140, headerName: "Postal #", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "manulifeBranch",
    width: 150,
    headerName: "Manulife B.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "brandedAs", width: 250, headerName: "Branded As", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "bank.brandNameOnRBC",
    width: 140,
    headerName: "RBC Acc Brand Name",
    floatingFilter: true,
    filter: SelectFilter,
    cellRenderer: YesNoCellRenderer,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
  },
  {
    field: "bank.t4aName",
    width: 250,
    headerName: "T4A Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "bank.t4aType",
    width: 150,
    headerName: "T4A Type",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "bank.st",
    width: 150,
    headerName: "HST",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "dates.contractCommencement",
    width: 140,
    headerName: "Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "associate.commissionSplit",
    width: 200,
    headerName: "Split",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "months", width: 140, headerName: "Months", floatingFilter: true, filter: "agNumberColumnFilter" },
  {
    field: "status",
    width: 140,
    headerName: "Status",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: STATUS_OPTIONS,
    },
  },
  {
    field: "bank.holdCommission",
    width: 150,
    headerName: "Commission Hold",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "billing.paymentMethod",
    width: 150,
    headerName: "Billing Method",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "billing.dataKey",
    width: 150,
    headerName: "Mone Key",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "nuvei",
    colId: "nuveiUserTokenId",
    width: 150,
    headerName: "Nuvei",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.nuvei?.[0]?.userTokenId,
  },
];

export const MARKETING_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "note",
    width: 80,
    headerName: "Notes",
    sortable: false,
    suppressMenu: true,
    cellRenderer: NoteCellRenderer,
  },
  {
    field: "risingStarLevel",
    width: 110,
    headerName: "Level",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "ttandExtension",
    width: 150,
    headerName: "Ext",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "agentType",
    width: 140,
    headerName: "Type",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGENT_TYPE_OPTIONS,
    },
  },
  {
    field: "agentId",
    width: 150,
    headerName: "Agent ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "businessEmail",
    width: 300,
    headerName: "Business Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 140,
    headerName: "Branch ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "marketing.firstname",
    width: 250,
    headerName: "MemberFirstName",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "marketing.lastname",
    width: 250,
    headerName: "MemberLastName",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "marketing.phone",
    width: 250,
    headerName: "MemberPhone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "marketing.email",
    width: 300,
    headerName: "MarketingEmail",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "marketing.streetAddress",
    width: 250,
    headerName: "MemberAddress",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "marketing.city",
    width: 250,
    headerName: "MemberCity",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "marketing.province",
    colId: "memberProvince",
    width: 160,
    headerName: "MemberProvince",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  {
    field: "marketing.postalCode",
    width: 250,
    headerName: "MemberPostalCode",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "websiteCode",
    width: 250,
    headerName: "Website Code",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "secretAgentString",
    width: 140,
    headerName: "Private Agent",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YES_NO_OPTIONS,
    },
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "licenses.tico",
    colId: "ticoNumber",
    headerName: "TICO Number",
    width: 150,
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.licenses?.tico,
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "licenses.bccp",
    colId: "bccpNumber",
    headerName: "BCCP Number",
    width: 150,
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.licenses?.bccp,
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "licenses.opc",
    colId: "opcNumber",
    headerName: "OPC Number",
    width: 150,
    floatingFilter: true,
    filter: "agTextColumnFilter",
    valueGetter: (params) => params.data?.licenses?.opc,
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "facebookUrl",
    width: 250,
    headerName: "MemberFacebook",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "twitterUrl",
    width: 250,
    headerName: "MemberTwitter",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "linkedinUrl",
    width: 250,
    headerName: "MemberLinkedIn",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "pinterestUrl",
    width: 250,
    headerName: "MemberPinterest",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "tiktokUrl",
    width: 250,
    headerName: "MemberTikTok",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "websiteURL",
    width: 250,
    headerName: "MemberWebsite",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "brandedAs",
    width: 250,
    headerName: "Branded As",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "marketingEmailsAllowedString",
    width: 150,
    headerName: "Email Mark. Allowed",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YES_NO_OPTIONS,
    },
  },
  {
    field: "marketingMailPiecesAllowedString",
    width: 150,
    headerName: "Direct Mail Pieces",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YES_NO_OPTIONS,
    },
  },
  {
    field: "streetAddress",
    width: 275,
    headerName: "Home Address",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "city", width: 175, headerName: "Home City", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "province",
    colId: "homeProvince",
    width: 140,
    headerName: "Home Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  { field: "postalCode", width: 140, headerName: "Home Postal #", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "dates.websiteCompletion",
    colId: "marketingB2CWebsiteCompletion",
    width: 220,
    headerName: "B2C Website Completion Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "agencyLogo",
    colId: "marketingAgencyLogo",
    width: 200,
    minWidth: 100,
    headerName: "Agency Logo",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: ({ value }: ICellRendererParams<AgentObj>) => (
      <Stack direction="row" alignItems="center" height="100%" minWidth={0}>
        <Thumbnail
          imageURL={value}
          style={{
            width: "100%",
            maxWidth: 100,
            height: 44,
            minWidth: 0,
            boxSizing: "border-box",
            padding: "4px 8px",
            objectFit: "contain",
          }}
        />
      </Stack>
    ),
  },
  {
    field: "status",
    colId: "marketingStatus",
    width: 140,
    headerName: "Status",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: STATUS_OPTIONS,
    },
  },
  {
    field: "dates.terminationDate",
    colId: "marketingTerminationDate",
    width: 150,
    headerName: "Termination Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "ttandEmail",
    colId: "marketingTtandEmail",
    width: 260,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
  },
  {
    field: "isLuxeAgent",
    colId: "marketingLuxeEnrolled",
    width: 190,
    headerName: "LUXE Program Enrolled",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "isLuxeCertifiedAdvisor",
    colId: "marketingLuxeCertified",
    width: 190,
    headerName: "LUXE Certified",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    cellRenderer: YesNoCellRenderer,
  },
];

export const SPECIALIST_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "agentId",
    width: 150,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 180,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "usClientlinqBranch",
    width: 180,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "agentType",
    width: 150,
    headerName: "Type",
    floatingFilter: true,
    filter: SelectFilter,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: { suppressFilterButton: true },
    filterParams: { options: AGENT_TYPE_OPTIONS },
  },
  { field: "agreement", width: 150, headerName: "Agreement", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "ttandEmail",
    width: 150,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  {
    field: "cellPhone",
    width: 150,
    headerName: "Cell Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "ttandExtension",
    width: 140,
    headerName: "Ext.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter", hide: true },
  {
    field: "province",
    width: 140,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  {
    field: "months",
    width: 140,
    headerName: "Months",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    hide: true,
  },
  {
    field: "destination.canada",
    width: 150,
    headerName: "D - Canada",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.usa",
    width: 150,
    headerName: "D - USA",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.caribbean",
    width: 150,
    headerName: "D - Jamaica",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.california",
    width: 150,
    headerName: "D - California",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.centralAmerica",
    width: 150,
    headerName: "D - Central America",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.florida",
    width: 150,
    headerName: "D - Florida",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.lasvegas",
    width: 150,
    headerName: "D - Las Vegas",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.mexico",
    width: 150,
    headerName: "D - Mexico",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.asia",
    width: 150,
    headerName: "D - Asia",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.africa",
    width: 150,
    headerName: "D - Africa",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.australia",
    width: 150,
    headerName: "D - Australia",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.europe",
    width: 150,
    headerName: "D - Europe",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.newzealand",
    width: 150,
    headerName: "D - NewZealand",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "destination.southAmerica",
    width: 150,
    headerName: "D - South America",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.aironly",
    width: 150,
    headerName: "E - Air Only",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.cruise",
    width: 150,
    headerName: "E - Cruise",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.ecoAdventure",
    width: 150,
    headerName: "E - Eco Adventure",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.luxury",
    width: 150,
    headerName: "E - Luxury",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.motorCoach",
    width: 150,
    headerName: "E - Motor Coach",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.sunAndBeaches",
    width: 150,
    headerName: "E - Sun & Beaches",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.tours",
    width: 150,
    headerName: "E - Tours",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.wedding",
    width: 150,
    headerName: "E - Weddings",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.groups",
    width: 150,
    headerName: "E - Groups",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.families",
    width: 150,
    headerName: "E - Families",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.couples",
    width: 150,
    headerName: "E - Couples",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.glbtq",
    width: 150,
    headerName: "E - GLBTQ",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.singles",
    width: 150,
    headerName: "E - Singles",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
  {
    field: "expertise.students",
    width: 150,
    headerName: "E - Students",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: EXPERTISE_LEVEL_OPTIONS,
    },
    cellRenderer: SpecialistTextCellRenderer,
  },
];

export const LEARNER_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "agentId",
    width: 150,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 180,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "usClientlinqBranch",
    width: 180,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "agentType",
    width: 150,
    headerName: "Type",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGENT_TYPE_OPTIONS,
    },
  },
  {
    field: "agreement",
    width: 150,
    headerName: "Agreement",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGREEMENT_OPTIONS,
    },
  },
  {
    field: "ttandEmail",
    width: 150,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  {
    field: "cellPhone",
    width: 150,
    headerName: "Cell Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "ttandExtension",
    width: 140,
    headerName: "Ext.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter", hide: true },
  {
    field: "province",
    width: 140,
    headerName: "Province",
    floatingFilter: true,
    filter: SelectFilter,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  {
    field: "months",
    width: 140,
    headerName: "Months",
    floatingFilter: true,
    filter: "agNumberColumnFilter",
    hide: true,
  },
  {
    field: "learnMoreDestinations.canada",
    width: 150,
    headerName: "D - Canada",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.canada,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.usa",
    width: 150,
    headerName: "D - USA",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.usa,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.caribbean",
    width: 150,
    headerName: "D - Jamaica",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.caribbean,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.california",
    width: 150,
    headerName: "D - California",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.california,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.centralAmerica",
    width: 150,
    headerName: "D - Central America",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.centralAmerica,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.florida",
    width: 150,
    headerName: "D - Florida",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.florida,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.lasvegas",
    width: 150,
    headerName: "D - Las Vegas",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.lasvegas,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.mexico",
    width: 150,
    headerName: "D - Mexico",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.mexico,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.asia",
    width: 150,
    headerName: "D - Asia",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.asia,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.africa",
    width: 150,
    headerName: "D - Africa",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.africa,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.australia",
    width: 150,
    headerName: "D - Australia",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.australia,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.europe",
    width: 150,
    headerName: "D - Europe",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.europe,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.newzealand",
    width: 150,
    headerName: "D - NewZealand",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.newzealand,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreDestinations.southAmerica",
    width: 150,
    headerName: "D - South America",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreDestinations?.southAmerica,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.aironly",
    width: 150,
    headerName: "E - Air Only",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.aironly,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.cruise",
    width: 150,
    headerName: "E - Cruise",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.cruise,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.ecoAdventure",
    width: 150,
    headerName: "E - Eco Adventure",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.ecoAdventure,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.luxury",
    width: 150,
    headerName: "E - Luxury",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.luxury,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.motorCoach",
    width: 150,
    headerName: "E - Motor Coach",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.motorCoach,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.sunAndBeaches",
    width: 150,
    headerName: "E - Sun & Beaches",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.sunAndBeaches,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.tours",
    width: 150,
    headerName: "E - Tours",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.tours,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.wedding",
    width: 150,
    headerName: "E - Weddings",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.wedding,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.groups",
    width: 150,
    headerName: "E - Groups",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.groups,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.families",
    width: 150,
    headerName: "E - Families",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.families,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.couples",
    width: 150,
    headerName: "E - Couples",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.couples,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.glbtq",
    width: 150,
    headerName: "E - GLBTQ",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.glbtq,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.singles",
    width: 150,
    headerName: "E - Singles",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.singles,
    cellRenderer: YesNoCellRenderer,
  },
  {
    field: "learnMoreProducts.students",
    width: 150,
    headerName: "E - Students",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: YESNO_BOOLEAN,
    },
    valueGetter: (params) => params.data?.learnMoreProducts?.students,
    cellRenderer: YesNoCellRenderer,
  },
];

export const CERTIFIED_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "agentId",
    width: 150,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 180,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "usClientlinqBranch",
    width: 180,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "agentType",
    width: 150,
    headerName: "Type",
    floatingFilter: true,
    filter: SelectFilter,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: { suppressFilterButton: true },
    filterParams: { options: AGENT_TYPE_OPTIONS },
  },
  { field: "agreement", width: 150, headerName: "Agreement", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "ttandEmail",
    width: 300,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  {
    field: "cellPhone",
    width: 150,
    headerName: "Cell Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "ttandExtension",
    width: 140,
    headerName: "Ext.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter", hide: true },
  {
    field: "province",
    width: 200,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  {
    field: "certifications",
    width: 800,
    headerName: "Certifications",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: CERTIFICATION_OPTIONS,
      partialMatch: true,
    },
    cellRenderer: ({ value }: ICellRendererParams<AgentObj>) => {
      return (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography component={"span"} noWrap>
            {value?.map((cert: string) => CERTIFICATIONS[parseInt(cert)]).join(", ") || "-"}
          </Typography>
        </Stack>
      );
    },
  },
];

export const FBGROUPS_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "agentId",
    width: 150,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 180,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "usClientlinqBranch",
    width: 180,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "cellPhone",
    width: 150,
    headerName: "Cell Phone",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "ttandExtension",
    width: 140,
    headerName: "Ext.",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter", hide: true },
  {
    field: "agentType",
    width: 150,
    headerName: "Type",
    floatingFilter: true,
    filter: SelectFilter,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: { suppressFilterButton: true },
    filterParams: { options: AGENT_TYPE_OPTIONS },
  },
  { field: "agreement", width: 150, headerName: "Agreement", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "ttandEmail",
    width: 250,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  {
    field: "province",
    width: 200,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  {
    field: "fbGroups",
    width: 650,
    headerName: "Facebook Groups",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: FB_GROUPS_OPTIONS,
      partialMatch: true,
    },
  },
];

export const RESIGNED_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "agentId",
    width: 150,
    headerName: "ID",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
  {
    field: "name",
    width: 200,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "title",
    width: 110,
    headerName: "Title",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "firstname",
    width: 200,
    headerName: "First Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    hide: true,
  },
  {
    field: "clientlinqBranch",
    width: 180,
    headerName: "Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "usClientlinqBranch",
    width: 180,
    headerName: "US Branch",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "agentType",
    width: 150,
    headerName: "Type",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGENT_TYPE_OPTIONS,
    },
  },
  {
    field: "agreement",
    width: 220,
    headerName: "Agreement",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: AGREEMENT_OPTIONS,
    },
  },
  {
    field: "ttandEmail",
    width: 150,
    headerName: "TTAND Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  { field: "cellPhone", width: 150, headerName: "Cell Phone", floatingFilter: true, filter: "agTextColumnFilter" },
  { field: "ttandExtension", width: 140, headerName: "Ext.", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "associate.reportingPrimaryAgent",
    width: 200,
    headerName: "PA for AA",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  {
    field: "streetAddress",
    width: 275,
    headerName: "Home Address",
    floatingFilter: true,
    filter: "agTextColumnFilter",
  },
  { field: "city", width: 175, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "province",
    width: 140,
    headerName: "Province",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: PROVINCE_OPTIONS,
    },
  },
  { field: "postalCode", width: 140, headerName: "Postal #", floatingFilter: true, filter: "agTextColumnFilter" },
  { field: "brandedAs", width: 250, headerName: "Branded As", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "dates.contractCommencement",
    width: 140,
    headerName: "Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  {
    field: "dates.terminationDate",
    width: 150,
    headerName: "Termination Date",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: OptionalValueCellRenderer,
  },
  { field: "months", width: 140, headerName: "Months", floatingFilter: true, filter: "agNumberColumnFilter" },
  {
    field: "status",
    width: 200,
    headerName: "Status",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: STATUS_OPTIONS,
    },
  },
];

export const STAFF_COLUMNS: ColDef<AgentObj>[] = [
  {
    field: "name",
    width: 300,
    headerName: "Name",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: NameCellRenderer,
    pinned: "left",
  },
  {
    field: "status",
    width: 150,
    headerName: "Status",
    filter: SelectFilter,
    floatingFilter: true,
    suppressMenu: true,
    floatingFilterComponent: SelectFloatingFilter,
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    filterParams: {
      options: STATUS_OPTIONS,
    },
  },
  { field: "city", width: 200, headerName: "City", floatingFilter: true, filter: "agTextColumnFilter" },
  { field: "cellPhone", width: 150, headerName: "Cell Phone", floatingFilter: true, filter: "agTextColumnFilter" },
  {
    field: "ttandEmail",
    width: 350,
    headerName: "Email",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: EmailCellRenderer,
    cellRendererParams: {
      replaceText: STANDARD_DOMAIN,
    },
  },
  {
    field: "agentId",
    minWidth: 200,
    width: 250,
    flex: 1,
    headerName: "Agent Id",
    floatingFilter: true,
    filter: "agTextColumnFilter",
    cellRenderer: LinkedCellRenderer,
  },
];
